import { useState, useEffect } from "react";
import { supabase, BRAND, BRAND_LIGHT, MUTED, LINE, SURFACE, TEXT, pageTitle, text, btn, btnSm, chip, errorBanner, savedText, SHADOW_MODAL, SCRIM, INK, FAINT, WHITE, NEUTRAL_BG } from "./constants";
import { track } from "./lib/analytics";
import { JDFormatted, CloseButton, BackButton, stripRules } from "./shared";
import { aiFetch } from "./lib/ai";
import { useIsMobile } from "./lib/responsive";
import { logoHtml, LOGO_FONT_LINK } from "./Logo";
import { PageLoader } from "./LogoLoader";
import CultureProfile, { hasCultureProfile } from "./CultureProfile";
import { cultureQuestions, cleanAnswers, isAnswered, SCALE_VALUES, scaleLabel, CULTURE_VERSION } from "./lib/culture";

// Todas las preguntas en los dos idiomas: dan el alto de cada bloque de la
// tarjeta (31/10). Cada bloque apila, invisibles, todos sus textos posibles en
// la misma celda, así mide lo que mide el más largo y la tarjeta no cambia de
// alto al pasar de una pregunta a otra (ni al cambiar de idioma).
const ALL_QS = [...cultureQuestions("es"), ...cultureQuestions("en")];
const ALL_TITLES = ALL_QS.map(q => q.title);
const ALL_TEXTS = ALL_QS.map(q => q.text);
const ALL_OPTIONS = ALL_QS.filter(q => q.type === "scale").flatMap(q => [q.a, q.b]);
const Stack = ({ all, current, style }) => (
  <div style={{ display: "grid", alignItems: "start" }}>
    {all.map((s, i) => <div key={i} aria-hidden="true" style={{ gridArea: "1 / 1", visibility: "hidden", ...style }}>{s}</div>)}
    <div style={{ gridArea: "1 / 1", ...style }}>{current}</div>
  </div>
);

const CultureQuestionnaireView = ({ t, lang, setView, selectedClientId, companyName, onCultureDocSaved, isAdmin = false }) => {
  const isMobile = useIsMobile();
  const QUESTIONS = cultureQuestions(lang);
  const N = QUESTIONS.length;

  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // 0 = intro, 1…N = preguntas, N + 1 = generando / hecho
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showExistingDoc, setShowExistingDoc] = useState(false);
  const [editingExisting, setEditingExisting] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const noClientMsg = lang === "es" ? "Elige un cliente en «Cliente visible» para ver o guardar su cultura." : "Pick a client in “Viewing client” to see or save its culture.";
  const saveFailMsg = lang === "es" ? "No se pudo guardar. Inténtalo de nuevo." : "Couldn't save. Please try again.";
  // Sin cliente elegido no se lee ni se guarda nada. Antes caía en el
  // client_id 2 (Meetzy, la demo) y se podía sobrescribir su documento.
  const saveCulture = async () => {
    if (!selectedClientId) { setSaveErr(noClientMsg); return false; }
    const { data, error } = await supabase.from("culture_docs").upsert({
      client_id: selectedClientId, answers: { ...answers, _v: CULTURE_VERSION }, generated_doc: generatedDoc, updated_at: new Date().toISOString()
    }, { onConflict: "client_id" }).select("client_id");
    const ok = !error && !!data && data.length > 0;
    if (!ok) console.error("Error saving culture doc:", error);
    setSaveErr(ok ? "" : saveFailMsg);
    return ok;
  };

  // Load existing doc from Supabase on mount
  useEffect(() => {
    if (!selectedClientId) return;
    supabase.from("culture_docs").select("generated_doc, answers").eq("client_id", selectedClientId).maybeSingle()
      .then(({ data }) => {
        if (data?.generated_doc) setGeneratedDoc(data.generated_doc);
        // Las respuestas v1 (p1…p13, "A"/"B") se descartan: el cuestionario es otro
        if (data?.answers) setAnswers(cleanAnswers(data.answers));
      });
  }, [selectedClientId]);

  const current = QUESTIONS[step - 1];
  const progress = step > 0 && step <= QUESTIONS.length ? step : null;
  const allAnswered = QUESTIONS.every(q => isAnswered(q, answers[q.id]));

  const generateDoc = async () => {
    setGenerating(true);
    setStep(N + 1);
    // Antes al prompt solo llegaba la letra ("A"/"B"), sin el texto de la
    // opción: la IA no sabía qué había contestado la empresa. El perfil
    // calculado NO va al prompt (31/10): el documento lo ve el candidato y el
    // perfil (con su estilo de Archetypes) es interno; solo el texto elegido.
    const answersText = QUESTIONS.map(q => {
      if (q.type === "open") return `${q.title}: ${answers[q.id] || ""}`;
      const v = answers[q.id];
      const strength = Math.abs(v) === 2 ? (lang === "es" ? "claramente" : "clearly") : (lang === "es" ? "más bien" : "leaning");
      return `${q.title}: ${q.text}\n→ ${strength}: ${v < 0 ? q.a : q.b}`;
    }).join("\n\n");
    const isEs = lang === "es";
    const prompt = isEs
      ? `Eres un consultor de executive search redactando el documento "Cultura de ${companyName || "la empresa"}" para ser compartido con candidatos ejecutivos antes de su primera entrevista.\n\nTienes las respuestas de un cuestionario que ha rellenado la empresa. Transforma las respuestas en un dossier directo, honesto y descriptivo.\n\nTONO: Descriptivo, nunca aspiracional ni marketiniano. Si la empresa exige mucho, dilo claramente. Si la disponibilidad es alta, dilo. El candidato necesita esta información para tomar una decisión adulta.\n\nRESPUESTAS:\n${answersText}\n\nGenera el dossier con exactamente estas secciones en markdown:\n\n## Quiénes somos\n## Cómo trabajamos\n## Feedback y desarrollo\n## Qué te vas a encontrar el primer día\n## Qué valoran de verdad\n## Qué te ofrece la empresa\n## Esto no es para ti si...\n\nLa sección "Esto no es para ti si..." debe ser una lista corta de 3-4 perfiles concretos que no encajarían, redactada de forma directa. Es la sección más importante del documento. No uses líneas separadoras (---) entre secciones. Escribe todo en español.`
      : `You are an executive search consultant writing the "${companyName || "the company"} Culture Document" to be shared with executive candidates before their first interview.\n\nYou have the answers to a questionnaire filled out by the company. Transform the answers into a direct, honest, descriptive dossier.\n\nTONE: Descriptive, never aspirational or marketing-speak. If the company demands a lot, say so clearly. If availability expectations are high, say so. The candidate needs this information to make an adult decision.\n\nANSWERS:\n${answersText}\n\nGenerate the dossier with exactly these sections in markdown:\n\n## Who we are\n## How we work\n## Feedback & development\n## What you'll find on day one\n## What we truly value\n## What we offer\n## This is not for you if...\n\nThe "This is not for you if..." section must be a short list of 3-4 concrete profiles that wouldn't fit, written directly. It is the most important section. Do not use separator lines (---) between sections. Write everything in English.`;
    try {
      const res = await aiFetch({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ max_tokens: 2000, messages: [{ role: "user", content: prompt }] })
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      setGeneratedDoc(stripRules(text));
      if (text) track("culture_doc_generated", { company: companyName || "" });
    } catch(e) { console.error(e); setGeneratedDoc(lang === "es" ? "Error al generar el documento." : "Error generating document."); }
    setGenerating(false);
  };

  const saveDoc = async () => {
    setSaving(true);
    if (await saveCulture()) { setSaved(true); if (onCultureDocSaved) onCultureDocSaved(generatedDoc); }
    setSaving(false);
  };

  const inputStyle = { width: "100%", padding: "10px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
  // Móvil: tarjetas con menos relleno, botones de 44 px y el modal como hoja
  // a pantalla completa. En escritorio no cambia nada.
  const cardPad = isMobile ? "18px 16px" : "32px 36px";
  const mBtn = isMobile ? { height: 40, padding: "0 14px" } : null;
  const navBtn = isMobile ? { height: 44, padding: "0 18px" } : null;
  const closeDoc = () => setShowExistingDoc(false);
  const ghostSm = { ...btn.ghost, ...btnSm, display: "flex", alignItems: "center", gap: 5, ...mBtn };
  // Perfil de cultura en su PROPIA tarjeta, debajo de la del documento (31/10):
  // nunca dentro de la misma caja. El mapa de Archetypes, solo para el admin.
  const profileCard = hasCultureProfile(answers) && (
    <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: cardPad, marginTop: 16 }}>
      <CultureProfile answers={answers} lang={lang} showMap={isAdmin} />
    </div>
  );
  // Tarjeta de pregunta: mismos bloques y mismas alturas en las 14 pantallas
  const optPad = isMobile ? "14px 14px" : "16px 20px";
  const qTitleStyle = { ...text.label, lineHeight: 1.5 };
  const qTextStyle = { fontSize: 15, color: INK, lineHeight: isMobile ? 1.65 : 1.75, fontWeight: 500 };
  const optTextStyle = { fontSize: 13, color: INK, lineHeight: 1.65 };

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 760 }}>
      <BackButton onClick={() => setView("dashboard")} label={t.back} ariaLabel={t.back} style={{ marginBottom: isMobile ? 8 : 24 }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 16 : 28, ...(isMobile ? { gap: 10 } : null) }}>
        <h1 style={pageTitle(isMobile)}>{lang === "es" ? "Documento de cultura" : "Culture document"}</h1>
        {progress && <span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>{progress} / {QUESTIONS.length}</span>}
      </div>

      {(!selectedClientId || saveErr) && (
        <div style={saveErr ? { ...errorBanner, marginBottom: 16 } : { background: SURFACE, border: "1.5px solid " + LINE, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: TEXT, lineHeight: 1.6, marginBottom: 16 }}>{saveErr || noClientMsg}</div>
      )}

      {/* Progress bar */}
      {progress && (
        <div style={{ background: NEUTRAL_BG, borderRadius: 4, height: 4, marginBottom: isMobile ? 16 : 32, overflow: "hidden" }}>
          <div style={{ background: BRAND, height: 4, width: `${(progress / QUESTIONS.length) * 100}%`, transition: "width 0.3s ease", borderRadius: 4 }} />
        </div>
      )}

      {/* Intro */}
      {step === 0 && (
        <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: cardPad }}>
          <div style={{ fontSize: isMobile ? 15 : 15, color: TEXT, lineHeight: isMobile ? 1.7 : 1.8, marginBottom: isMobile ? 16 : 24 }}>
            {lang === "es"
              ? `Este cuestionario de ${N} preguntas nos permite generar automáticamente un documento de cultura honesto y directo para compartir con candidatos antes de la entrevista. El objetivo es que quien no encaje se autoexcluya, y quien sí encaje llegue convencido. Además, calcula el perfil de cultura de la empresa en 6 dimensiones${isAdmin ? " y su posición en el mapa de Archetypes, el mismo que usan los candidatos" : ""}.`
              : `This ${N}-question survey lets us automatically generate an honest, direct culture document to share with candidates before the interview. The goal: those who don't fit self-select out, and those who do arrive convinced. It also scores your culture on 6 dimensions${isAdmin ? " and places it on the Archetypes map, the same one candidates use" : ""}.`}
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: isMobile ? 20 : 28 }}>⏱ {lang === "es" ? "Tiempo estimado: 10-12 minutos" : "Estimated time: 10-12 minutes"}</div>
          <div style={isMobile ? { display: "flex", flexDirection: "column", gap: 10 } : { display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={() => setStep(1)} style={{ ...btn.primary, ...(isMobile ? { width: "100%", height: 48, padding: 0, fontSize: 15, fontFamily: "inherit" } : null) }}>
              {lang === "es" ? "Empezar →" : "Start →"}
            </button>
            {generatedDoc && (
              <button onClick={() => setShowExistingDoc(true)} style={{ ...btn.ghost, ...(isMobile ? { width: "100%", height: 48, padding: 0, fontFamily: "inherit" } : null) }}>
                {lang === "es" ? "Ver documento" : "Document"}
              </button>
            )}
          </div>
        </div>
      )}
      {step === 0 && profileCard}

      {/* Existing doc viewer/editor modal */}
      {showExistingDoc && generatedDoc && (
        <div style={isMobile
            ? { position: "fixed", inset: 0, background: WHITE, zIndex: 500, display: "flex", flexDirection: "column" }
            : { position: "fixed", inset: 0, background: SCRIM, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={isMobile ? undefined : closeDoc}>
          <div style={isMobile
              ? { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 16px calc(24px + env(safe-area-inset-bottom))", boxSizing: "border-box" }
              : { background: WHITE, borderRadius: 16, padding: "32px 36px", width: "100%", maxWidth: 660, maxHeight: "88vh", overflowY: "auto", boxShadow: SHADOW_MODAL }}
            onClick={e => e.stopPropagation()}>
            <div style={isMobile
                ? { position: "sticky", top: 0, zIndex: 1, background: WHITE, margin: "0 -16px 16px", padding: "calc(10px + env(safe-area-inset-top)) 16px 10px", borderBottom: `1px solid ${LINE}`, display: "flex", flexDirection: "column", gap: 10 }
                : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              {isMobile ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ ...text.cardTitle, minWidth: 0 }}>{lang === "es" ? "Documento de cultura" : "Culture document"}</span>
                  <CloseButton onClick={closeDoc} label={lang === "es" ? "Cerrar" : "Close"} />
                </div>
              ) : (
                <span style={text.cardTitle}>{lang === "es" ? "Documento de cultura" : "Culture document"}</span>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {saved && <span style={savedText}>{lang === "es" ? "✓ Guardado" : "✓ Saved"}</span>}
                <button onClick={() => {
                  const win = window.open("", "_blank");
                  const body = stripRules(generatedDoc).split('\n').map(line => {
                    const t = line.trim();
                    if (!t) return '<div style="height:8px"></div>';
                    const hm = t.match(/^##\s+(.+)$/);
                    if (hm) return `<div style="font-size:11px;font-weight:800;color:${BRAND};text-transform:uppercase;letter-spacing:1px;margin-top:20px;margin-bottom:6px;">${hm[1]}</div>`;
                    if (t.startsWith('- ') || t.startsWith('• ')) return `<div style="display:flex;gap:8px;margin-bottom:4px;"><div style="width:4px;height:4px;border-radius:50%;background:${BRAND};margin-top:7px;flex-shrink:0;"></div><span style="font-size:12px;color:${INK};line-height:1.7;">${t.replace(/^[-•]\s*/,'')}</span></div>`;
                    return `<div style="font-size:12px;color:${INK};line-height:1.7;margin-bottom:3px;">${t}</div>`;
                  }).join('');
                  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><link href="${LOGO_FONT_LINK}" rel="stylesheet"><title>${companyName || "Culture"}</title><style>@page{margin:2cm}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}</style></head><body><div style="padding-bottom:18px;border-bottom:2px solid ${BRAND};margin-bottom:28px;"><div style="font-size:20px;font-weight:800;color:${INK};">${lang === "es" ? `Cultura de ${companyName || ""}` : `${companyName || ""} Culture Document`}</div></div>${body}<div style="margin-top:48px;padding-top:16px;border-top:1px solid ${LINE};font-size:10px;color:${FAINT};"><span style="display:inline-flex;align-items:center;gap:6px;">Powered by ${logoHtml(11)}</span></div></body></html>`);
                  win.document.close();
                  setTimeout(() => { win.focus(); win.print(); }, 400);
                }} style={ghostSm}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  PDF
                </button>
                <button onClick={async () => {
                  setSaving(true);
                  const ok = await saveCulture();
                  setSaving(false);
                  if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
                }} style={{ ...btn.primary, ...btnSm, ...(isMobile ? { height: 40, flex: 1 } : null) }}>
                  {saving ? "..." : (lang === "es" ? "Guardar" : "Save")}
                </button>
                <button onClick={() => setEditingExisting(v => !v)} style={{ ...(editingExisting ? btn.primary : btn.ghost), ...btnSm, ...(isMobile ? { height: 40, flex: 1 } : null) }}>
                  {editingExisting ? (lang === "es" ? "Vista" : "View") : (lang === "es" ? "Editar" : "Edit")}
                </button>
                {!isMobile && <CloseButton onClick={closeDoc} label={lang === "es" ? "Cerrar" : "Close"} />}
              </div>
            </div>
            {editingExisting ? (
              <textarea value={generatedDoc} onChange={e => setGeneratedDoc(e.target.value)} rows={22}
                style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: `1.5px solid ${BRAND}`, borderRadius: 10, fontSize: isMobile ? 16 : 13, lineHeight: 1.75, resize: "vertical", outline: "none", fontFamily: "inherit", color: INK }} />
            ) : (
              <JDFormatted text={generatedDoc} />
            )}
          </div>
        </div>
      )}

      {/* Preguntas */}
      {step >= 1 && step <= N && current && (
        <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: cardPad }}>
          {/* Cada bloque mide lo que su texto más largo (Stack): título,
              escenario y respuesta quedan siempre a la misma altura */}
          <div style={{ marginBottom: 10 }}><Stack all={ALL_TITLES} current={current.title} style={qTitleStyle} /></div>
          <div style={{ marginBottom: isMobile ? 18 : 28 }}><Stack all={ALL_TEXTS} current={current.text} style={qTextStyle} /></div>
          {/* Respuesta: las cajas A/B y la escala siempre se maquetan (dan el
              alto); en las abiertas quedan ocultas y el texto ocupa su sitio */}
          <div style={{ display: "grid", marginBottom: isMobile ? 20 : 28 }}>
            <div style={{ gridArea: "1 / 1", visibility: current.type === "scale" ? "visible" : "hidden" }} aria-hidden={current.type !== "scale"}>
              <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 10 : 12, marginBottom: 16 }}>
                {["a", "b"].map(opt => {
                  const v = answers[current.id];
                  const on = current.type === "scale" && (opt === "a" ? v < 0 : v > 0);
                  return (
                    // Mismo borde y mismo relleno seleccionada o no: al elegir no se mueve nada
                    <div key={opt} style={{ padding: optPad, borderRadius: 10, border: `1.5px solid ${on ? BRAND : LINE}`, background: on ? BRAND_LIGHT : SURFACE, transition: "background 0.15s, border-color 0.15s" }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: on ? BRAND : MUTED, background: on ? WHITE : NEUTRAL_BG, borderRadius: 4, padding: "2px 8px", flexShrink: 0, marginTop: 1 }}>{opt.toUpperCase()}</span>
                        <div style={{ flex: 1, minWidth: 0 }}><Stack all={ALL_OPTIONS} current={current[opt] || ""} style={optTextStyle} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div role="radiogroup" aria-label={current.title} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 8 }}>
                {SCALE_VALUES.map(v => {
                  const on = current.type === "scale" && answers[current.id] === v;
                  return (
                    <button key={v} role="radio" aria-checked={on} tabIndex={current.type === "scale" ? 0 : -1} onClick={() => setAnswers(prev => ({ ...prev, [current.id]: v }))}
                      style={{ height: isMobile ? 44 : 40, borderRadius: 8, border: `1.5px solid ${on ? BRAND : LINE}`, background: on ? BRAND : WHITE, color: on ? WHITE : TEXT, fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", transition: "background 0.15s, border-color 0.15s, color 0.15s", padding: "0 6px" }}>
                      {scaleLabel(v, lang)}
                    </button>
                  );
                })}
              </div>
            </div>
            {current.type === "open" && (
              <textarea key={current.id} value={answers[current.id] || ""} onChange={e => setAnswers(prev => ({ ...prev, [current.id]: e.target.value }))}
                placeholder={lang === "es" ? "Escribe aquí..." : "Write here..."}
                style={{ ...inputStyle, gridArea: "1 / 1", height: "100%", minHeight: 0, resize: "none", lineHeight: 1.65 }}
                onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", ...(isMobile ? { gap: 10 } : null) }}>
            <BackButton onClick={() => setStep(s => s - 1)} label={t.back} ariaLabel={t.back} style={isMobile ? { minHeight: 44 } : null} />
            {step < N ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!isAnswered(current, answers[current.id])} style={{ ...(isAnswered(current, answers[current.id]) ? btn.primary : btn.disabled), ...navBtn, ...(isMobile ? { flex: 1 } : null) }}>
                {lang === "es" ? "Siguiente →" : "Next →"}
              </button>
            ) : (
              <button onClick={generateDoc} disabled={!allAnswered} style={{ ...(allAnswered ? btn.primary : btn.disabled), ...navBtn, ...(isMobile ? { flex: 1 } : null) }}>
                {lang === "es" ? "✦ Generar documento de cultura" : "✦ Generate culture document"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Generating / Result */}
      {step === N + 1 && (
        <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14, padding: cardPad }}>
          {generating ? (
            <PageLoader minHeight={200} appearDelay={0} label={lang === "es" ? "Generando vuestro documento de cultura..." : "Generating your culture document..."} />
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 14 : 20, ...(isMobile ? { gap: 8, flexWrap: "wrap" } : null) }}>
                <span style={text.cardTitle}>{lang === "es" ? "Documento de cultura" : "Culture document"}</span>
                <span style={chip(false)}>{lang === "es" ? "Generado con IA · editable" : "AI-generated · editable"}</span>
              </div>
              <textarea value={generatedDoc} onChange={e => setGeneratedDoc(e.target.value)} rows={isMobile ? 16 : 20}
                style={{ ...inputStyle, lineHeight: 1.7, resize: "vertical", marginBottom: 20, color: INK }}
                onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
              <div style={{ display: "flex", gap: isMobile ? 10 : 12, justifyContent: "flex-end" }}>
                <BackButton onClick={() => setStep(N)} label={t.back} ariaLabel={t.back} style={isMobile ? { minHeight: 44 } : null} />
                {saved ? (
                  <span style={{ ...savedText, alignSelf: "center" }}>{lang === "es" ? "✓ Guardado" : "✓ Saved"}</span>
                ) : (
                  <button onClick={saveDoc} disabled={saving} style={{ ...btn.primary, cursor: saving ? "wait" : "pointer", ...navBtn, ...(isMobile ? { flex: 1 } : null) }}>
                    {saving ? (lang === "es" ? "Guardando..." : "Saving...") : (lang === "es" ? "✓ Guardar documento" : "✓ Save document")}
                  </button>
                )}
              </div>
              {saved && <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}><BackButton onClick={() => setView("dashboard")} label={lang === "es" ? "Volver al inicio" : "Back to dashboard"} ariaLabel={lang === "es" ? "Volver al inicio" : "Back to dashboard"} /></div>}
            </>
          )}
        </div>
      )}
      {step === N + 1 && !generating && profileCard}
    </div>
  );
};

export default CultureQuestionnaireView;
